import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import verticalCPRChart from '../../../images/icons/vertical_CPR_chart.svg';

/**
 * Technical Tab Content - Shows CPR levels with visual bars and resistance/support
 */
const TechnicalTabContent = ({ pivotLevels }) => {
  const { theme } = useTheme();

  // Find classic pivot level
  const classicPivot = pivotLevels?.find(
    (p) => p.method === 'Classic' || p.pivotlevel_name === 'Classic' || p.name === 'Classic'
  );

  if (!classicPivot) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className={`text-sm ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
          No CPR data available
        </p>
      </div>
    );
  }

  const formatValue = (value) => {
    if (!value) return '-';
    return parseFloat(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="flex h-full">
      {/* Left Column - CPR Levels */}
      <div className="flex-[60] px-4">
        <h3 className="text-sm font-semibold mb-1 text-p300">
          CPR Levels
        </h3>
        <p className={`text-xs font-medium mb-8 ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
          Central Pivotal Range with resistance and support levels.
        </p>

        {/* CPR Visual Bars */}
        <div className="flex items-end justify-between mb-2 px-1">
          {/* S3 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              S3
            </span>
            <div className="w-1.5 h-[100px] bg-red" />
          </div>

          {/* Empty bars */}
          <div className="w-1.5 h-[100px] bg-n100" />
          <div className="w-1.5 h-[100px] bg-n100" />

          {/* S2 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              S2
            </span>
            <div className="w-1.5 h-[100px] bg-red" />
          </div>

          {/* Empty bar */}
          <div className="w-1.5 h-[100px] bg-n100" />

          {/* S1 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              S1
            </span>
            <div className="w-1.5 h-[100px] bg-red" />
          </div>

          {/* CPR Mini Graph - 3 SVG charts together */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">

            </span>
            <div className="flex gap-0.5">
              <img src={verticalCPRChart} alt="CPR" className="h-[100px]" />
              <img src={verticalCPRChart} alt="CPR" className="h-[100px]" />
              <img src={verticalCPRChart} alt="CPR" className="h-[100px]" />
            </div>
          </div>

          {/* R1 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              R1
            </span>
            <div className="w-1.5 h-[100px] bg-p150" />
          </div>

          {/* Empty bar */}
          <div className="w-1.5 h-[100px] bg-n100" />

          {/* R2 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              R2
            </span>
            <div className="w-1.5 h-[100px] bg-p150" />
          </div>

          {/* Empty bars */}
          <div className="w-1.5 h-[100px] bg-n100" />
          <div className="w-1.5 h-[100px] bg-n100" />

          {/* R3 */}
          <div className="flex flex-col items-center">
            <span className="text-sm font-semibold mb-0.5 text-n500">
              R3
            </span>
            <div className="w-1.5 h-[100px] bg-p150" />
          </div>
        </div>

        {/* CPR Details */}
        <div className="flex justify-between mt-6 px-2">
          <div className="text-center">
            <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
              CPR Trend
            </p>
            <p className="text-xs font-medium text-n500">
              Trendy
            </p>
          </div>
          <div className="text-center">
            <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
              Virgin CPR
            </p>
            <p className="text-xs font-medium text-n500">
              No
            </p>
          </div>
          <div className="text-center">
            <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
              Band Range
            </p>
            <p className="text-xs font-medium text-n500">
              Very Narrow
            </p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className={`w-px ${theme === 'dark' ? 'bg-n100' : 'bg-n100'}`} />

      {/* Right Column - Resistance and Support */}
      <div className="flex-[40] px-4">
        {/* Resistance */}
        <h3 className="text-sm font-semibold mb-2 text-p300">
          Resistance
        </h3>
        <div className="space-y-2.5 mb-4">
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Resistance R1</span>
            <span className="text-sm font-semibold text-p150">
              {formatValue(classicPivot.r1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Resistance R2</span>
            <span className="text-sm font-semibold text-p150">
              {formatValue(classicPivot.r2)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Resistance R3</span>
            <span className="text-sm font-semibold text-p150">
              {formatValue(classicPivot.r3)}
            </span>
          </div>
        </div>

        {/* Support */}
        <h3 className="text-sm font-semibold mb-2 mt-8 text-p300">
          Support
        </h3>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Support S1</span>
            <span className="text-sm font-semibold text-red">
              {formatValue(classicPivot.s1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Support S2</span>
            <span className="text-sm font-semibold text-red">
              {formatValue(classicPivot.s2)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium text-n500`}>• Support S3</span>
            <span className="text-sm font-semibold text-red">
              {formatValue(classicPivot.s3)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalTabContent;
